import React, { useState, useCallback, useMemo } from "react";
import { Formik } from "formik";
import Modal from "../../../ui-elements/Modal/Modal";
import { IPepWatchFormValues, PepWatchListData, PepWatchModalProps, parsedFieldData } from "../../../types/PepTypes";
import { submitSinglePerson, submitBulkData } from "../PepWatchHelpers/PepWatchApi";
import { useFileUpload } from "../PepWatchHelpers/PepWatchHooks";
import { FileUploadSection } from "../PepWatchHelpers/FileUpload";
import { PepWatchAddPersonForm } from "../PepWatchHelpers/PepWatchAddPersonForm";
import Button from "../../../ui-elements/Button/Button";
import Bugsnag from "@bugsnag/js";
import { validationSchema } from "../PepWatchHelpers/PepWatchAddPersonForm";
import Table from "../../../ui-elements/Table/Table";
import { formatFullName } from "../../../services/pepWatchUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/pro-solid-svg-icons";

// Interface for failed submission results
interface FailedSubmissionResult {
  failedRecords: PepWatchListData[]; // Changed from parsedFieldData[] to PepWatchListData[]
  successCount: number;
  totalCount: number;
  hasPartialSuccess: boolean;
}

const PepWatchAddPersonsListModal: React.FC<PepWatchModalProps> = ({
  show,
  close,
  setToastMessage,
  setToastShow,
  setToastType,
  onSuccess,
}) => {
  const [isFileUploadOpen, setIsFileUploadOpen] = useState(false);
  const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);
  
  // New state to track failed submission results
  const [failedSubmissionResult, setFailedSubmissionResult] = useState<FailedSubmissionResult | null>(null);
  
  // Use the enhanced file upload hook
  const { 
    uploadedFile, 
    isLoading, 
    validData, 
    invalidData, 
    handleFileUpload, 
    resetFileUpload, 
    validationProgress,
    validationStats,
    isValidating,
    stopValidation
  } = useFileUpload();

  // Enhanced submit handler for single person with better error handling
  const handleSubmit = useCallback(async (values: IPepWatchFormValues) => {
    try {
      const responseBody = await submitSinglePerson(values);

      if (responseBody.success) {
        // Handle different success scenarios with more specific messages
        if (responseBody.result?.descr?.includes("already exists in watchlist")) {
          setToastMessage("Vöktun á viðskiptavini þegar til staðar");
          setToastType("alert");
          setToastShow(true);
        } else if (responseBody.result?.descr?.includes("No value present")) {
          setToastMessage(responseBody.result.descr);
          setToastType("alert");
          setToastShow(true);
        } else if(responseBody.result?.descr?.includes("First name and last name required if no ssn")) {
          setToastMessage("Nafn og eftirnafn nauðsynlegt ef engin kennitala er til staðar");
          setToastType("alert");
          setToastShow(true);
        } else {
          onSuccess();
          setToastMessage("Vöktun á viðskiptavini bætt við");
          setToastType("success");
          setToastShow(true);
          close();
        }
      } else {
        // Handle API errors more gracefully
        const errorMessage = responseBody.result?.descr ?? "Vöktun á viðskiptavini ekki bætt við";
        setToastMessage(errorMessage);
        setToastType("alert");
        setToastShow(true);
      }
    } catch (error) {
      console.error('Error submitting single person:', error);
      
      if (error instanceof Error) {
        // Log error details for debugging
        Bugsnag.notify(error, event => {
          event.addMetadata('singlePersonSubmit', {
            values: values,
            timestamp: new Date().toISOString()
          });
        });
        
        // Show specific error message if available
        const errorMessage = error.message || "Vöktun á viðskiptavini ekki bætt við";
        setToastMessage(errorMessage);
        setToastType("error");
        setToastShow(true);
      } else {
        // Handle non-Error objects
        setToastMessage("Óþekkt villa kom upp");
        setToastType("error");
        setToastShow(true);
      }
    }
  }, [setToastMessage, setToastType, setToastShow, onSuccess, close]);

  // Enhanced bulk submit handler with failed records tracking
  const handleBulkSubmit = useCallback(async (data: parsedFieldData[]) => {
    if (data.length === 0) {
      setToastMessage("Engin gild gögn til að senda");
      setToastType("alert");
      setToastShow(true);
      return;
    }

    setIsSubmittingBulk(true);
    // Clear any previous failed submission results
    setFailedSubmissionResult(null);
    
    try {
      // Add some validation before submitting
      const validRecords = data.filter(record => 
        record && typeof record === 'object' && 
        (record.PersonId || (record.Name && record.DateOfBirth && record.CountryCode))
      );
      
      if (validRecords.length !== data.length) {
        console.warn(`Filtered out ${data.length - validRecords.length} invalid records`);
      }
      
      const responseBody = await submitBulkData(validRecords);

      if (responseBody.success) {
        // Check for non-success result code
        if (responseBody.result?.result && responseBody.result.result !== 1) {
          setToastMessage(responseBody.result.descr ?? "Villa kom upp");
          setToastType("error");
          setToastShow(true);
        // Check if there were any failed records
        } else if(responseBody.result?.failed && responseBody.result.failed.length > 0) {
          const failedRecords = responseBody.result.failed;
          const successCount = validRecords.length - failedRecords.length;
          
          console.warn("Some records failed to submit:", failedRecords);
          
          // Store failed submission results for display
          setFailedSubmissionResult({
            failedRecords: failedRecords,
            successCount: successCount,
            totalCount: validRecords.length,
            hasPartialSuccess: successCount > 0
          });
          
          // Show appropriate toast message
          if (successCount > 0) {
            setToastMessage(`Vöktun á ${successCount} ${successCount === 1 ? 'viðskiptavini' : 'viðskiptavinum'} bætt við, en ${failedRecords.length} tókst ekki`);
            setToastType("alert");
          } else {
            setToastMessage(`Vöktun á ${failedRecords.length} ${failedRecords.length === 1 ? 'viðskiptavini' : 'viðskiptavinum'} tókst ekki`);
            setToastType("error");
          }
          setToastShow(true);
          
          // Call onSuccess if at least some records were successful
          if (successCount > 0) {
            onSuccess();
          }
        } else {
          // All records were successful
          onSuccess();
          setToastMessage(`Vöktun á ${validRecords.length} ${validRecords.length === 1 ? 'viðskiptavini' : 'viðskiptavinum'} bætt við`);
          setToastType("success");
          setToastShow(true);
          resetFormAndClose();
        }
      } else {
        // Handle bulk submission errors
        const errorMessage = responseBody.result?.descr ?? "Vöktun á viðskiptavinum ekki bætt við";
        setToastMessage(errorMessage);
        setToastType("alert");
        setToastShow(true);
      }
    } catch (error) {
      console.error('Error submitting bulk data:', error);
      
      if (error instanceof Error) {
        // Log bulk submission errors with more context
        Bugsnag.notify(error, event => {
          event.addMetadata('bulkSubmit', {
            recordCount: data.length,
            timestamp: new Date().toISOString(),
            validationSessionId: validationStats.sessionId
          });
        });
        
        const errorMessage = error.message || "Villa við að bæta við viðskiptavinum";
        setToastMessage(errorMessage);
        setToastType("error");
        setToastShow(true);
      } else {
        setToastMessage("Óþekkt villa kom upp við að bæta við viðskiptavinum");
        setToastType("error");
        setToastShow(true);
      }
    } finally {
      setIsSubmittingBulk(false);
    }
  }, [setToastMessage, setToastType, setToastShow, onSuccess, validationStats.sessionId]);

  // Handler to retry failed records
  const handleRetryFailedRecords = useCallback(async () => {
    if (!failedSubmissionResult?.failedRecords) return;
    
    // Convert PepWatchListData back to parsedFieldData format for retry
    const retryData: parsedFieldData[] = failedSubmissionResult.failedRecords.map((record: PepWatchListData) => ({
      PersonId: record.id.personId,
      Name: `${record.personFirstName} ${record.personMiddleName || ''} ${record.personLastName}`.trim(),
      DateOfBirth: record.personDateOfBirth,
      CountryCode: record.personCountryCode,
      Gender: record.personGender,
      // Add any other fields that might be needed for retry
    }));
    
    // Retry submission with converted data
    await handleBulkSubmit(retryData);
  }, [failedSubmissionResult, handleBulkSubmit]);

  // Handler to clear failed submission results and start over
  const handleClearFailedResults = useCallback(() => {
    setFailedSubmissionResult(null);
  }, []);

  // Enhanced reset and close handler
  const resetFormAndClose = useCallback(() => {
    // Stop any ongoing validation
    if (isValidating) {
      stopValidation();
    }
    
    // Reset file upload state
    resetFileUpload();
    
    // Reset local state
    setIsFileUploadOpen(false);
    setIsSubmittingBulk(false);
    setFailedSubmissionResult(null); // Clear failed submission results
    
    // Close modal
    close();
  }, [isValidating, stopValidation, resetFileUpload, close]);

  // Memoized form component for better performance
  const subFormComponent = useMemo(() => {
    return (props: { values: IPepWatchFormValues }) => (
      <PepWatchAddPersonForm values={props.values} onClose={resetFormAndClose} />
    );
  }, [resetFormAndClose]);

  // Enhanced modal body with failed records display
  const modalBody = useCallback(() => {
    const red = "#DB0033";
    const green = "#038600";
    return (
      <div className="KCL_PepWatchAddPersonsListModal">
        <h3>{!failedSubmissionResult ? "Bæta við viðskiptavinum" : "Niðurstöður sendingar"}</h3>

        {/* Show failed submission results if available */}
        {failedSubmissionResult && (
          <>
            <span style={{marginBottom: '10px', display: 'block'}} className="paragraph-xsmall paragraph--italic">Gögn gætu þegar verið í vöktun eða eitthvað fór úrskeiðis við sendingu.</span>
            {/* Display failed records in a table */}
            <div style={{ marginBottom: 15 }}>
              <Table
                aSide={
                  <div style={{ fontSize: 14, display: "flex", columnGap: "40px", rowGap: "10px", flexWrap: "wrap" }}>
                    <div>
                      <FontAwesomeIcon icon={faCheck} style={{ color: green }} />{` Tókst: 
                      ${failedSubmissionResult.successCount} ${
                      failedSubmissionResult.successCount === 1 ? 'færsla' : 'færslur'
                      }`}
                    </div>
                    <div>
                      <FontAwesomeIcon icon={faTimes} style={{ color: red }} />{` Mistókst: ${
                      failedSubmissionResult.failedRecords.length
                      } ${
                      failedSubmissionResult.failedRecords.length === 1 ? 'færsla' : 'færslur'
                      }`}
                    </div>
                  </div>
                }
                withPagination={"auto"}
                paginationConfig={{ usePortal: true, itemsPerPage: 10  }}
                data={failedSubmissionResult.failedRecords}
                columns={[
                  {
                    title: "Kennitala",
                    renderCell: ({ id }: PepWatchListData) => id?.personId || "",
                    textAlign: "left",
                  },
                  {
                    title: "Nafn",
                    renderCell: ({ personFirstName, personMiddleName, personLastName }: PepWatchListData) =>
                      formatFullName({
                        firstName: personFirstName,
                        middleName: personMiddleName,
                        lastName: personLastName,
                      }),
                    textAlign: "left",
                  },
                  {
                    title: "Fæðingardagur",
                    renderCell: ({ personDateOfBirth }: PepWatchListData) => personDateOfBirth || "",
                    textAlign: "left",
                  },
                  {
                    title: "Land",
                    renderCell: ({ personCountryCode }: PepWatchListData) => personCountryCode || "",
                    textAlign: "left",
                  },
                  {
                    title: "Vöktun PEP",
                    renderCell: ({ id }: PepWatchListData) => (
                      <FontAwesomeIcon
                        icon={id?.personId ? faCheck : faTimes}
                        style={{ color: id?.personId ? green : red }}
                      />
                    ),
                    textAlign: "center",
                  },
                  {
                    title: "Vöktun alþj. válistar",
                    renderCell: ({ personFirstName, personLastName, personCountryCode, personDateOfBirth }) => {
                      const isValid = personFirstName && personLastName && personCountryCode && personDateOfBirth;
                      return (
                        <FontAwesomeIcon icon={isValid ? faCheck : faTimes} style={{ color: isValid ? green : red }} />
                      );
                    },
                    textAlign: "center",
                  },
                  {
                    title: "Kyn",
                    renderCell: ({ personGender }) => (personGender === "Male" ? "Karl" : personGender === "Female" ? "Kona" : ""),
                    textAlign: "left",
                  },
                  {
                    title: "Athugasemd",
                    renderCell: ({ reference }) => {
                      if (reference.length < 11) {
                        return reference;
                      } else {
                        return reference.slice(0, 10) + "...";
                      }
                    },
                    textAlign: "left",
                  },
                  {
                    title: "Ástæða",
                    renderCell: (item: any) => item.reason || "",
                    textAlign: "left",
                  },
                ]}
              />
            </div>

            {/* Action button for failed records */}
            <Button buttonType="primary" size="lg" onClick={resetFormAndClose}>
              Loka
            </Button>
          </>
        )}

        {/* File upload section - hide when showing failed results */}
        {!failedSubmissionResult && (
          <FileUploadSection
            isFileUploadOpen={isFileUploadOpen}
            setIsFileUploadOpen={setIsFileUploadOpen}
            uploadedFile={uploadedFile}
            isLoading={isLoading}
            validData={validData}
            invalidData={invalidData}
            onFileUpload={handleFileUpload}
            onResetFile={resetFileUpload}
            onStopValidation={stopValidation}
            onBulkSubmit={handleBulkSubmit}
            validationProgress={validationProgress}
            isValidating={isValidating}
            isSubmittingBulk={isSubmittingBulk}
            validationStats={validationStats}
          />
        )}

        {/* Single person form - only show when no file is uploaded and no failed results */}
        {!uploadedFile && !failedSubmissionResult && (
          <>
            <h6>Upplýsingar um einstakling</h6>
            <Formik
              initialValues={{
                name: "",
                ssn: "",
                foreign: false,
                dateOfBirth: "",
                countryCode: "",
                pep: true,
                sanctions: false,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              component={subFormComponent}
              validateOnMount={false}
            />
          </>
        )}
      </div>
    );
  }, [
    failedSubmissionResult,
    handleRetryFailedRecords,
    handleClearFailedResults,
    isSubmittingBulk,
    resetFormAndClose,
    validationProgress,
    validationStats,
    isFileUploadOpen,
    uploadedFile,
    isLoading,
    validData,
    invalidData,
    handleFileUpload,
    resetFileUpload,
    stopValidation,
    handleBulkSubmit,
    isValidating,
    handleSubmit,
    subFormComponent
  ]);

  return (
    <Modal
      className="pep-watch-modal"
      show={show}
      onHide={resetFormAndClose}
      hasCloseButton
      body={modalBody()}
    />
  );
};

export default PepWatchAddPersonsListModal;